/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/chips/styles.css';
import type { StatusIndicator, StatusSize } from 'BomdisoftComponents/chips/render';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { ColorVariant } from 'BomdisoftComponents/types';
import { capitalizeFirstLetters } from '@bomdisoft/utilities';
import { type MouseEvent, type ReactNode } from 'react';

export type { StatusIndicator, StatusSize } from 'BomdisoftComponents/chips/render';

export type StatusProps = {
  text: string;
  variant?: ColorVariant;
  indicator?: StatusIndicator;
  /** A leading glyph. It takes the indicator's place rather than sitting beside
   * it, matching Alert, where passing an `icon` implies `indicator='icon'`. */
  icon?: ReactNode;
  size?: StatusSize;
  capitalize?: boolean;
  rounded?: boolean;
  outlined?: boolean;
  /** A persistent "chosen" state for a clickable chip — a filter tag that's
   * currently active, say. Distinct from hover: hover is momentary pointer
   * feedback, selected stays on until the caller toggles it off. Only
   * meaningful alongside `onClick`. */
  selected?: boolean;
  className?: string;
  testId?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export const Status = ({ text, variant = 'default', indicator = 'fill', icon, size = 'medium', capitalize = true, rounded, outlined, selected, className, testId, onClick }: StatusProps) => {
  const content = capitalize ? capitalizeFirstLetters(text) : text;
  const classes = joinClasses('bomdisoft-chip', className);
  const hasIcon = icon !== undefined && icon !== false && icon !== null;
  // `outlined` and `rounded` were gated on the fill indicator, which an icon
  // replaces; gate them on the dot instead so an icon chip can still be either.
  const isDot = !hasIcon && indicator === 'dot';
  const markers = {
    'aria-pressed': onClick ? (selected ?? false) : undefined,
    'data-clickable': onClick ? '' : undefined,
    'data-indicator': hasIcon ? 'icon' : indicator === 'dot' ? 'dot' : undefined,
    'data-outlined': !isDot && outlined ? '' : undefined,
    'data-rounded': !isDot && rounded ? '' : undefined,
    'data-selected': selected ? '' : undefined,
    'data-size': size === 'small' ? 'small' : undefined,
    'data-testid': testId,
    'data-variant': variant,
  };
  const marker = hasIcon ? (
    <span aria-hidden='true' className='bomdisoft-chip-icon'>
      {icon}
    </span>
  ) : indicator === 'dot' ? (
    <span aria-hidden='true' className='bomdisoft-chip-dot' />
  ) : null;

  if (onClick) {
    return (
      <button {...markers} className={classes} onClick={onClick} type='button'>
        {marker}
        {content}
      </button>
    );
  }
  return (
    <span {...markers} className={classes}>
      {marker}
      {content}
    </span>
  );
};
